<script>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { mapGetters } from 'vuex';
import { useRoute } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import ProductCatalogPanel from 'dashboard/components/widgets/conversation/ProductCatalogPanel.vue';
import TodayTasksPanel from './TodayTasksPanel.vue';
import { LocalStorage } from 'shared/helpers/localStorage';

const CHAT_LIST_VIEW_MODE_KEY = 'joota:conversation-view-mode';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
    ProductCatalogPanel,
    TodayTasksPanel,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const route = useRoute();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      route,
    };
  },
  data() {
    return {
      showSearchModal: false,
      activeConversationViewMode:
        LocalStorage.get(CHAT_LIST_VIEW_MODE_KEY) || 'list',
      isBoardConversationModalOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      currentRole: 'getCurrentRole',
    }),
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    showConversationList() {
      if (this.isBoardView) return true;
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      if (this.isBoardView) return false;
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isBoardView() {
      return this.activeConversationViewMode === 'board';
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (this.currentRole === 'marketing') {
        return false;
      }
      if (this.isBoardView || !this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
    shouldShowProductCatalogSidebar() {
      if (this.currentRole === 'marketing') {
        return false;
      }
      if (this.isBoardView || !this.currentChat.id || !this.inbox) {
        return false;
      }

      const { is_product_catalog_panel_open: isProductCatalogPanelOpen } =
        this.uiSettings;
      return isProductCatalogPanelOpen;
    },
    shouldShowTodayTasksPanel() {
      return (
        this.isOnExpandedLayout &&
        (this.route.query.assigneeType === 'me' ||
          ['inbox_view', 'inbox_view_conversation'].includes(
            this.route.name
          )) &&
        !this.isBoardView &&
        !this.label &&
        !this.teamId &&
        !this.conversationType &&
        !this.foldersId
      );
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    if (this.currentRole !== 'marketing') {
      this.$store.dispatch('agents/get');
      this.$store.dispatch('portals/index');
    }
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      return this.findConversationById(this.conversationId);
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    onConversationViewModeChange(mode) {
      this.activeConversationViewMode = mode;
    },
    async onBoardConversationOpen(conversation) {
      if (!conversation?.id) return;

      const existingChat = this.findConversationById(conversation.id);
      const selectedConversation = existingChat || conversation;

      await this.$store.dispatch('setActiveChat', {
        data: selectedConversation,
      });
      this.isBoardConversationModalOpen = true;
      emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    },
    closeBoardConversationModal() {
      this.isBoardConversationModalOpen = false;
      if (!this.conversationId) {
        this.$store.dispatch('clearSelectedState');
      }
    },
    findConversationById(id) {
      const conversationId = parseInt(id, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0 bg-white dark:bg-n-background">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
      @view-mode-change="onConversationViewModeChange"
      @board-conversation-open="onBoardConversationOpen"
    />
    <TodayTasksPanel v-if="shouldShowTodayTasksPanel" />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id && currentRole !== 'marketing'" />
    </ConversationBox>
    <ConversationSidebar
      v-if="shouldShowSidebar && !isBoardConversationModalOpen"
      :current-chat="currentChat"
    />
    <ProductCatalogPanel
      v-if="shouldShowProductCatalogSidebar && !isBoardConversationModalOpen"
      :chat="currentChat"
    />
    <CmdBarConversationSnooze />

    <div
      v-if="isBoardConversationModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-4 backdrop-blur-sm"
      @click.self="closeBoardConversationModal"
    >
      <section
        class="relative flex h-[94vh] w-[96vw] min-w-0 overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-2xl shadow-slate-950/20 dark:border-n-weak dark:bg-n-solid-2"
      >
        <button
          type="button"
          class="absolute right-4 top-4 z-20 grid size-9 place-content-center rounded-full bg-white/90 text-n-slate-11 shadow-sm ring-1 ring-n-weak backdrop-blur hover:bg-n-alpha-1 dark:bg-n-solid-3/90 dark:hover:bg-n-alpha-2"
          @click="closeBoardConversationModal"
        >
          <span class="i-lucide-x size-5" />
        </button>
        <ConversationBox
          class="min-w-0 flex-1 [&.conversation-details-wrap]:!border-0"
          :inbox-id="currentChat.inbox_id || inboxId"
          :is-on-expanded-layout="false"
        >
          <SidepanelSwitch v-if="currentChat.id && currentRole !== 'marketing'" />
        </ConversationBox>
        <ConversationSidebar
          v-if="currentChat.id && currentRole !== 'marketing'"
          class="w-[360px] shrink-0 border-l border-n-weak"
          :current-chat="currentChat"
        />
      </section>
    </div>
  </section>
</template>
